/*
 * The Housecall Pro jobs an accepted proposal becomes.
 *
 * One function, used twice: the API sends what it returns, and the proposal
 * page shows the same lines in the confirm before anything is created. Every
 * line is priced from the frozen document under the accepted selection, by
 * the same arithmetic the client's total came from, so the job agrees with
 * what the client accepted (see "What is written (phase 4)" in
 * irrigation.md).
 */
import type {
  HcpJobDraftDto,
  HcpJobLineDto,
  HcpJobTreeDto,
} from '../dto/hcp-job.dto.js'
import type {
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  ProposalDocumentDto,
  TreeScheduleBlock,
  TreeScheduleRow,
} from '../dto/proposal-document.dto.js'
import { JOB_TYPE_LABELS, isDeclinableJobType } from '../enums/job-type.enum.js'
import {
  priceIrrigationSite,
  quantityHundredths,
  unpriceableReason,
} from './irrigation-pricing.js'
import { formatMoneyValue, parseMoney } from './money.js'
import {
  irrigationScheduleBlocks,
  repairDropped,
  treeAmountCents,
  treeChoiceFor,
  type ProposalSelection,
} from './proposal-selection.js'

/** An HCP service a tree line can be made on, as Settings → Trees chose it. */
export interface TreeJobService {
  uuid: string
  name: string
}

/** The services tree lines go on: pruning (treatments included) and removal. */
export interface TreeJobServices {
  pruning: TreeJobService | null
  removal: TreeJobService | null
}

/** What a line comes to, rounded to the cent as HCP rounds a line. */
export function hcpLineCents(line: HcpJobLineDto): number {
  const hundredths = quantityHundredths(line.quantity) ?? 0
  const unitCents = parseMoney(line.unitPrice) ?? 0
  return Math.round((hundredths * unitCents) / 100)
}

/**
 * Every job the accepted proposal becomes: a tree job for each site with
 * tree work kept, and an irrigation job for each site with repairs kept.
 * Trees first, in the document's order.
 */
export function hcpJobDrafts(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
  treeServices: TreeJobServices,
): HcpJobDraftDto[] {
  const drafts: HcpJobDraftDto[] = []
  for (const block of document.blocks) {
    if (block.type !== 'treeSchedule') continue
    const draft = treeJob(document, block, selection, treeServices)
    if (draft !== null) drafts.push(draft)
  }
  for (const block of irrigationScheduleBlocks(document)) {
    const draft = irrigationJob(document, block, selection)
    if (draft !== null) drafts.push(draft)
  }
  return drafts
}

/**
 * A site's tree work as one line per tree, at the price the client accepted
 * for it, as the office writes its tree jobs. Taxed, because the proposal
 * taxed tree work whole.
 */
function treeJob(
  document: ProposalDocumentDto,
  block: TreeScheduleBlock,
  selection: ProposalSelection,
  services: TreeJobServices,
): HcpJobDraftDto | null {
  if (selection.excludedSites.includes(block.siteName)) return null
  const lines: HcpJobLineDto[] = []
  const trees: HcpJobTreeDto[] = []
  let accepted = 0
  for (const row of block.rows) {
    const cents = treeAmountCents(document, block.siteName, row, selection)
    if (cents <= 0) continue
    accepted += cents
    const removing = treeChoiceFor(block.siteName, row, selection) === 'REMOVE'
    const service = removing ? services.removal : services.pruning
    const work = treeWork(row, removing, selection)
    lines.push({
      kind: 'labor',
      name: service?.name ?? (removing ? 'Tree removal' : 'Tree pruning'),
      description: treeDescription(row, work),
      quantity: '1',
      unitPrice: formatMoneyValue(cents),
      taxable: true,
      serviceItem: service === null ? null : { id: service.uuid, type: 'service' },
    })
    trees.push({ treeId: row.treeId ?? null, label: row.label, ...work })
  }
  if (lines.length === 0) return null
  return draft(
    block.siteName,
    'TREES',
    lines,
    formatMoneyValue(accepted),
    [],
    trees,
  )
}

/** What is being done to one tree: the removal, or pruning and treatments. */
type TreeWork = Pick<HcpJobTreeDto, 'jobTypes' | 'treatments' | 'removal'>

/**
 * The work kept on one tree. "Other" is access, setup and clearing up, which
 * every tree has; the proposal leaves it out of its lists, and so does the
 * job.
 */
function treeWork(
  row: TreeScheduleRow,
  removing: boolean,
  selection: ProposalSelection,
): TreeWork {
  if (removing) return { jobTypes: [], treatments: [], removal: true }
  return {
    jobTypes: row.jobs
      .map((job) => job.type)
      .filter(
        (type) =>
          isDeclinableJobType(type) &&
          !selection.excludedJobTypes.includes(type),
      ),
    treatments: row.problems
      .filter(
        (problem) =>
          problem.amount !== null &&
          !selection.excludedProblems.includes(problem.name),
      )
      .map((problem) => problem.name),
    removal: false,
  }
}

/**
 * Which tree, and what is being done to it: "#4 · Live oak", then the kinds
 * of work kept and the treatments, or the removal and its notes.
 */
function treeDescription(row: TreeScheduleRow, work: TreeWork): string {
  const which = [row.label, row.species?.name, row.areaName]
    .filter((part): part is string => part !== null && part !== undefined && part !== '')
    .join(' · ')
  if (work.removal) {
    const notes = row.removal?.notes?.trim() ?? ''
    return [which, notes === '' ? 'Remove' : `Remove: ${notes}`].join('\n')
  }
  return [
    which,
    work.jobTypes.length === 0
      ? null
      : work.jobTypes.map((type) => JOB_TYPE_LABELS[type]).join(', '),
    work.treatments.length === 0
      ? null
      : `Treatments: ${work.treatments.join(', ')}`,
  ]
    .filter((part): part is string => part !== null)
    .join('\n')
}

/**
 * A site's kept repairs as the office itemizes its irrigation jobs: one
 * labor line for the visit's billed hours, each part as its price-book
 * material, and the drive charge.
 */
function irrigationJob(
  document: ProposalDocumentDto,
  block: IrrigationScheduleBlock,
  selection: ProposalSelection,
): HcpJobDraftDto | null {
  const { labor, driveCharge } = block.rateCard
  const kept = block.repairs.filter(
    (repair) =>
      !repairDropped(document, block.siteName, repair, selection) &&
      unpriceableReason(repair, block.rateCard) === null,
  )
  if (kept.length === 0) return null
  const priced = priceIrrigationSite(kept, block.rateCard)

  const lines: HcpJobLineDto[] = []
  if (labor !== null && priced.billedMinutes > 0) {
    lines.push({
      kind: 'labor',
      name: labor.name,
      description: kept
        .filter((repair) => (repair.laborMinutes ?? 0) > 0)
        .map(repairWords)
        .join('\n'),
      ...laborQuantity(priced.billedMinutes, labor.pricePerHour),
      taxable: labor.taxable,
      serviceItem:
        labor.hcpServiceUuid === null
          ? null
          : { id: labor.hcpServiceUuid, type: 'service' },
    })
  }
  for (const repair of kept) {
    for (const part of repair.parts) {
      lines.push({
        kind: 'materials',
        name: part.name,
        description: repairWords(repair),
        quantity: part.quantity,
        unitPrice: part.unitPrice,
        taxable: part.taxable,
        serviceItem:
          part.hcpMaterialUuid === null
            ? null
            : { id: part.hcpMaterialUuid, type: 'material' },
      })
    }
  }
  if (driveCharge !== null && priced.driveCharge !== null) {
    lines.push({
      kind: 'labor',
      name: `${driveCharge.town} Drive Charge`,
      description: '',
      quantity: '1',
      unitPrice: priced.driveCharge,
      taxable: driveCharge.taxable,
      serviceItem:
        driveCharge.hcpServiceUuid === null
          ? null
          : { id: driveCharge.hcpServiceUuid, type: 'service' },
    })
  }
  return draft(
    block.siteName,
    'IRRIGATION',
    lines,
    priced.amount,
    kept.map((repair) => repair.key),
    [],
  )
}

/** "Zone 2: Bad nozzle ×3", which repair a line is for. */
function repairWords(repair: IrrigationRepairRow): string {
  const count = repair.count > 1 ? ` ×${String(repair.count)}` : ''
  return `${repair.subject}: ${repair.kind}${count}`
}

/**
 * The labor line's quantity and price: billed hours at the hourly rate, which
 * quarter hours always are. A proposal sent before the billing rule may have
 * billed minutes that are not a whole hundredth of an hour; that one goes in
 * as one line at its labor price, so the job still matches the quote.
 */
function laborQuantity(
  billedMinutes: number,
  pricePerHour: string,
): { quantity: string; unitPrice: string } {
  if ((billedMinutes * 100) % 60 === 0) {
    return {
      quantity: String((billedMinutes * 100) / 60 / 100),
      unitPrice: pricePerHour,
    }
  }
  const rateCents = parseMoney(pricePerHour) ?? 0
  return {
    quantity: '1',
    unitPrice: formatMoneyValue(Math.round((billedMinutes * rateCents) / 60)),
  }
}

function draft(
  siteName: string,
  kind: HcpJobDraftDto['kind'],
  lines: HcpJobLineDto[],
  acceptedAmount: string,
  repairKeys: string[],
  trees: HcpJobTreeDto[],
): HcpJobDraftDto {
  const cents = lines.reduce((sum, line) => sum + hcpLineCents(line), 0)
  return {
    siteName,
    kind,
    lines,
    amount: formatMoneyValue(cents),
    acceptedAmount,
    repairKeys,
    trees,
  }
}
